import { createRouter, createWebHistory } from 'vue-router'

import AddEmployeeView from '@/views/Admin/employee-profiling-application/AddEmployeeView.vue'
import LinkEmployeeView from '@/views/Admin/employee-profiling-application/LinkEmployeeView.vue'
import AddSalaryGradeView from '@/views/Admin/employee-profiling-application/AddSalaryGradeView.vue'
import EmployeesView from '@/views/Admin/employee-profiling-application/EmployeesView.vue'
import LoginView from '@/views/LoginView.vue'
import VerifyEmail from '@/views/VerifyEmail.vue'
import ResetPassword from '@/views/ResetPassword.vue'
import EmployeeValidationsView from '@/views/Admin/employee-profiling-application/EmployeeValidationsView.vue'
import MyRegistrationsView from '@/views/Admin/employee-profiling-application/MyRegistrationsView.vue'
import LeaveApplicationForm from '@/views/Employee/LeaveApplicationForm.vue'
import LeaveDashboardView from '@/views/Employee/LeaveDashboardView.vue'
import LeaveBalancesView from '@/views/Admin/leave-application/LeaveBalancesView.vue'
import LeaveApplicationsDashboardView from '@/views/Admin/leave-application/LeaveApplicationsDashboardView.vue'
import LeaveApproverForm from '@/views/Admin/leave-application/LeaveApproverForm.vue'
import EmployeeReopenRequestsView from '@/views/Admin/employee-profiling-application/EmployeeReopenRequestsView.vue'
import EditEmployeeRequestsView from '@/views/Admin/employee-profiling-application/EditEmployeeRequestsView.vue'
import DashboardView from '@/views/Admin/DashboardView.vue'

// ================= ROLE MAPPINGS =================
// Mapping exact IDs from your "positions" table
const ROLES = {
  SYSTEM_ADMIN: 0,
  DIRECTOR: 1,
  ARD: 2,
  CHIEF: 3,
  HR_ADMIN: 8
}

// Access Groups
const SUPER_ADMIN_ACCESS = [ROLES.SYSTEM_ADMIN];
const ADMIN_ACCESS = [ROLES.SYSTEM_ADMIN, ROLES.HR_ADMIN];
const CHIEF_ACCESS = [ROLES.SYSTEM_ADMIN, ROLES.CHIEF, ROLES.DIRECTOR, ROLES.ARD]; 
const MANAGEMENT_ACCESS = [...ADMIN_ACCESS, ...CHIEF_ACCESS];

const routes = [
  {
    path: '/',
    name: 'login-view',
    component: LoginView,
    meta: { requiresAuth: false }
  },
  {
    path: '/verify-email',
    name: 'VerifyEmail',
    component: VerifyEmail,
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword, // Update path if needed
    meta: { requiresAuth: false } // <-- THIS IS THE KEY
  },

  // ================= ADMIN & HR ROUTES =================
  {
    path: '/dashboard',
    name: 'dashboard-view',
    component: DashboardView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/employees',
    name: 'employees-view',
    component: EmployeesView,
    meta: { requiresAuth: true, allowedPositions: MANAGEMENT_ACCESS }
  }, 
  {
    path: '/add-employee',
    name: 'add-employee-view',
    component: AddEmployeeView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/add-salary-grade',
    name: 'add-salary-grade-view',
    component: AddSalaryGradeView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/link-employee',
    name: 'link-employee-view',
    component: LinkEmployeeView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/admin/dtr-upload',
    name: 'DtrUpload',
    component: () => import('../views/Admin/dtr-application/DtrUpload.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/admin/dtr-batch-upload',
    name: 'DtrBatchUpload',
    component: () => import('../views/Admin/dtr-application/DtrBatchUpload.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/admin/audit-logs',
    name: 'AuditLogs',
    component: () => import('../views/Admin/AuditLogsView.vue'),
    // strictPosition: HR Admin position only, no role=admin bypass — the HR
    // Assistant must not reach the audit log. Matches backend position_id:0,8.
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS, strictPosition: true }
  },
  {
    path: '/employees-signup-requests',
    name: 'employee-validations-view',
    component: EmployeeValidationsView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/unlock-requests',
    name: 'employee-reopen-requests-view',
    component: EmployeeReopenRequestsView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/edit-requests',
    name: 'EditRequests',
    component: EditEmployeeRequestsView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/201management',
    name: '201-management-view',
    component: () => import('../views/Admin/Employee201FilesView.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/201files/:id',
    name: '201-file-view',
    component: () => import('../views/Admin/Employee201FileView.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/201queue',
    name: '201-queue-view',
    component: () => import('../views/Admin/Review201QueueView.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    // HR Manager only — strictPosition keeps the HR Assistant (role = admin) out, matching the API.
    path: '/service-record-queue',
    name: 'service-record-queue-view',
    component: () => import('../views/Admin/ServiceRecordQueueView.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS, strictPosition: true }
  },
  {
    // the archive was only ever the decided half of the queue
    path: '/201archive',
    redirect: '/201queue'
  },
  {
    path: '/pdsform',
    name: 'pdsform',
    component: () => import('../views/Admin/digital-201-application/PdsFormView.vue'),
    meta: { requiresAuth: true, notAllowedPositions: SUPER_ADMIN_ACCESS }
  },
  {
    path: '/hr/dtr/review',
    name: 'HRDtrReview',
    component: () => import('../views/Admin/dtr-application/HrDtrReview.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/hr/dtr/validation',
    name: 'HrDisputeValidation',
    component: () => import('../views/Admin/HrDisputeValidation.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  // ================= GENERAL EMPLOYEE ROUTES =================
  // If allowedPositions is omitted, ALL authenticated users can access these
  {
    path: '/my-profile',
    name: 'my-profile-view',
    component: () => import('../views/Employee/MyProfileView.vue'),
    meta: { requiresAuth: true, notAllowedPositions: SUPER_ADMIN_ACCESS }
  },
  {
    path: '/my-201-file',
    name: 'my-201-file-view',
    component: () => import('../views/Employee/My201FileView.vue'),
    meta: { requiresAuth: true }
  },
  {
    // the standalone history page is now a section inside My 201 File
    path: '/my-pds-history',
    redirect: '/my-201-file'
  },
  {
    path: '/my-registrations',
    name: 'my-registrations-view',
    component: MyRegistrationsView,
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    path: '/my-leave-requests',
    name: 'leave-dashboard-view',
    component: LeaveDashboardView,
    meta: { requiresAuth: true, notAllowedPositions: SUPER_ADMIN_ACCESS }
  },
  {
    path: '/apply-leave',
    name: 'leave-application-form',
    component: LeaveApplicationForm,
    meta: { requiresAuth: true }
  },
  {
    path: '/ipcrform',
    name: 'ipcrform',
    component: () => import('../views/Admin/digital-201-application/IpcrFormView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/salnform',
    name: 'salnform',
    component: () => import('../views/Admin/digital-201-application/SalnFormView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/employee/dtr',
    name: 'EmployeeDtr',
    component: () => import('../views/EmployeeDtr.vue'),
    meta: { requiresAuth: true }
  },

  // ================= CHIEF & MANAGEMENT ROUTES =================
  {
    path: '/leave-management',
    name: 'leave-applications-dashboard-view',
    component: LeaveApplicationsDashboardView,
    meta: { requiresAuth: true, allowedPositions: MANAGEMENT_ACCESS }
  }, 
  {
    path: '/leave-action/:id',
    name: 'leave-approver-form',
    component: LeaveApproverForm,
    meta: { requiresAuth: true, allowedPositions: MANAGEMENT_ACCESS }
  },
  {
    path: '/chief-dtr-disputes',
    name: 'ChiefDtrDisputes',
    component: () => import('../views/Admin/dtr-application/ChiefDtrDisputes.vue'),
    meta: { requiresAuth: true, allowedPositions: CHIEF_ACCESS }
  },
  {
    path: '/chief/dtr/employees',
    component: () => import('@/views/Admin/dtr-application/ChiefDtremployees.vue'),
    meta: { requiresAuth: true, allowedPositions: CHIEF_ACCESS }
  },
  {
    path: '/chief/dtr/review/:employeeId',
    name: 'ChiefDtrReview',
    component: () => import('@/views/Admin/dtr-application/ChiefDtrReview.vue'),
    meta: { requiresAuth: true, allowedPositions: CHIEF_ACCESS }
  },
  {
    path: '/admin/training-masterlist',
    name: 'HrTrainingMasterlist',
    component: () => import('@/views/HRtraining.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },

  // ================= TESTING & FALLBACKS =================
  {
    path: '/leave-balances',
    name: 'leave-balances',
    component: LeaveBalancesView,
    meta: { requiresAuth: true , allowedPositions: ADMIN_ACCESS } 
  },
  {
    path: '/employee/my-trainings',
    name: 'EmployeeTrainings',
    component: () => import('@/views/Employee/EmployeeTrainings.vue'),
    meta: { requiresAuth: true }
  },
  {
    // HR only. The API behind this page is already behind
    // role_or_position:admin,0,8 — this guard stops an employee reaching a
    // page whose every request would 403 and render as a blank queue.
    path: '/hr/training-approvals',
    name: 'HrTrainingApprovals',
    component: () => import('@/views/HRtrainingApprovals.vue'),
    meta: { requiresAuth: true, allowedPositions: ADMIN_ACCESS }
  },
  {
    // The Director's own screen, not an HR one — in-house certificates are
    // issued in their name, so they are the only one who can release them.
    // The controller re-checks this on every request; this guard only keeps
    // the page out of the wrong person's sidebar.
    path: '/training-certificates/sign',
    name: 'TrainingCertificateSigning',
    component: () => import('@/views/Admin/TrainingCertificateSigning.vue'),
    meta: { requiresAuth: true, allowedPositions: [ROLES.SYSTEM_ADMIN, ROLES.DIRECTOR] }
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/'
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// ================= NAVIGATION GUARD =================
router.beforeEach((to, from, next) => {
  // 1. Fetch user from localStorage
  const userData = JSON.parse(localStorage.getItem('hrdos_user')) || null;
  
  // 2. Safely extract position_id and user role
  let userPositionId = null;
  let userRole = null;

  // District heads recognized by plantilla title (SVTRO, TRO I, PURO, ...)
  // carry this flag from the backend (employee.is_district_chief). They get
  // the same access as a Chief even though their position_id is not 3.
  let isDistrictChief = false;

  if (userData) {
    userRole = userData.role;

    // If they have an employee record with a position_id, use it
    if (userData.employee && userData.employee.position_id) {
      userPositionId = Number(userData.employee.position_id);
    }
    // Fallback: the one true System Admin account has no employee record of its own.
    else if (userRole === 'superadmin') {
      userPositionId = ROLES.SYSTEM_ADMIN;
    }

    isDistrictChief = !!(userData.employee && userData.employee.is_district_chief);
  }

  // 3. Check if the route needs authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  
  if (requiresAuth) {
    // ibalik sa login page if hindi naka login
    if (!userData) {
      return next({ name: 'login-view' });
    }

    const allowedPositions = to.meta.allowedPositions;
    const notAllowedPositions = to.meta.notAllowedPositions;

    // Handle explicitly NOT allowed positions (like superadmin trying to open a PDS)
    if (notAllowedPositions && notAllowedPositions.includes(userPositionId)) {
      if (SUPER_ADMIN_ACCESS.includes(userPositionId)) {
        return next({ name: 'dashboard-view' });
      }
      return next({ name: 'my-profile-view' });
    }

    // 4. If route has restrictions, check if the user is authorized
    if (allowedPositions) {
      
      // Default check: is their position ID in the array?
      let hasAccess = allowedPositions.includes(userPositionId);

      // Backend Match: If the user has the 'admin' role, and this route allows HR_ADMIN (8), grant them access!
      // strictPosition routes (approval/decision pages) opt out of this
      // bypass so the HR Assistant (role=admin, other position) is kept out,
      // matching the backend position_id:0,8 guard on those endpoints.
      if (!to.meta.strictPosition && userRole === 'admin' && allowedPositions.includes(ROLES.HR_ADMIN)) {
        hasAccess = true;
      }

      // A district head (SVTRO / TRO I / PURO, flagged by the backend) gets
      // Chief access anywhere a Chief is allowed. The APIs they hit are still
      // scoped to their own office, so this only opens the same pages a Chief
      // Administrative Officer sees.
      if (!hasAccess && isDistrictChief && allowedPositions.includes(ROLES.CHIEF)) {
        hasAccess = true;
      }

      // If they still don't have access, redirect them safely
      if (!hasAccess) {
        if (SUPER_ADMIN_ACCESS.includes(userPositionId) || userRole === 'admin') {
          return next({ name: 'dashboard-view' });
        } else if (ADMIN_ACCESS.includes(userPositionId)) {
          return next({ name: 'dashboard-view' });
        } else if (CHIEF_ACCESS.includes(userPositionId)) {
          return next({ name: 'my-profile-view' });
        } else {
          return next({ name: 'my-profile-view' });
        }
      }
    }
  }

  // Allow navigation if all checks pass
  next();
})

export default router